import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { FileText, Sparkles, Save, Copy as CopyIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { api } from "../../lib/api";
import TranslateButton from "../../components/TranslateButton";

/**
 * Phase 22 — Legal pages admin editor with AI-draft support.
 * Public pages render the saved markdown at /legal/{slug}.
 */
const TABS = [
  { slug: "aviso_legal", label: "Aviso legal" },
  { slug: "privacidad", label: "Privacidad" },
  { slug: "cookies", label: "Cookies" },
  { slug: "terminos_y_condiciones", label: "Términos y condiciones" },
];

export default function AdminLegal() {
  const [active, setActive] = useState("aviso_legal");
  const [markdown, setMarkdown] = useState("");
  const [aiWarning, setAiWarning] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`/admin/legal/pages/${active}`)
      .then((r) => {
        setMarkdown(r.data?.markdown || "");
        setAiWarning(null);
      })
      .catch(() => setMarkdown(""));
  }, [active]);

  const generate = async () => {
    setBusy(true);
    const t = toast.loading("IA redactando borrador…");
    try {
      const { data } = await api.post("/admin/ai/legal/generate", {
        page: active,
        // Company details are now loaded server-side from `settings`. We only
        // pass overrides when the admin explicitly types something in the UI,
        // which currently is never — kept for future extensibility.
      });
      setMarkdown(data.markdown || "");
      setAiWarning(
        data.warning ||
          "Borrador generado por IA. Revisa con un asesor legal antes de publicar.",
      );
      toast.success("Borrador generado", { id: t });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail === "ai_timeout"
          ? "IA tardó demasiado"
          : "IA no disponible",
        { id: t },
      );
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    if (!markdown.trim()) return toast.error("No hay contenido para copiar");
    try {
      await navigator.clipboard.writeText(markdown);
      toast.success("Contenido copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const save = async () => {
    if (!markdown.trim()) return toast.error("El contenido no puede estar vacío");
    setSaving(true);
    try {
      await api.put("/admin/legal/pages", { slug: active, markdown });
      toast.success("Página guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-legal" className="space-y-4 max-w-5xl">
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-[#00953B]" />
        <h1 className="text-3xl font-black tracking-tight">Páginas legales</h1>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-neutral-200">
        {TABS.map((t) => (
          <button
            key={t.slug}
            data-testid={`admin-legal-tab-${t.slug}`}
            onClick={() => setActive(t.slug)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${
              active === t.slug
                ? "border-[#00953B] text-[#00953B] font-semibold"
                : "border-transparent text-neutral-600 hover:text-neutral-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={generate}
          disabled={busy}
          data-testid="admin-legal-ai-generate"
          variant="outline"
          className="gap-1 rounded-full border-[#00953B] text-[#00953B]"
        >
          <Sparkles className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} />
          Generar borrador con IA
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="admin-legal-save"
          className="bg-[#00953B] hover:bg-[#007a30] gap-1"
        >
          <Save className="h-4 w-4" /> Guardar
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={copyAll}
          disabled={!markdown.trim()}
          data-testid="admin-legal-copy-all"
          className="gap-1 rounded-full h-9"
        >
          <CopyIcon className="h-4 w-4" /> Copiar todo
        </Button>
        <TranslateButton
          value={markdown}
          testid="admin-legal-translate"
          label="Traducir borrador"
          className="border border-neutral-200"
        />
      </div>
      {/* Mandatory warning — always visible in the admin editor, per Phase 25 */}
      <div
        data-testid="admin-legal-ai-warning"
        className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3 flex items-start gap-2"
      >
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          <strong>Borrador generado por IA.</strong>{" "}
          {aiWarning ||
            "Revisa con un asesor legal antes de publicar."}
        </span>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="text-xs font-semibold text-neutral-500 mb-1">Markdown</div>
          <Textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            rows={22}
            data-testid="admin-legal-editor"
            className="font-mono text-xs"
          />
        </div>
        <div>
          <div className="text-xs font-semibold text-neutral-500 mb-1">Previsualización</div>
          <div
            data-testid="admin-legal-preview"
            className="rounded-2xl border bg-white p-5 prose prose-sm max-w-none prose-headings:font-black prose-h2:mt-6 prose-h2:mb-2 prose-h3:mt-4 prose-a:text-[#00953B] prose-table:text-xs prose-th:bg-[#F1FAF3] prose-th:font-semibold prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-th:border prose-td:border prose-th:border-neutral-200 prose-td:border-neutral-200"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
            >
              {markdown || "*Sin contenido — genera un borrador o escribe uno.*"}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}
